import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FINANCE_HINTS, taxCodeByCode, type Contact, type SalesInvoice, type TaxMode } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { ContactPicker } from '../../components/ContactPicker';
import { InfoHint } from '../../components/InfoHint';
import { bpText, parseBp } from '../../money';
import { lineErrorText } from '../offers/lineErrors';
import { RecipientCard } from '../offers/RecipientCard';
import { useUnits } from '../offers/LinesEditor';
import { draftFromInvoiceLine, emptyInvoiceLine, InvoiceLinesEditor, invoiceLinesBody, invoiceLinesProblem, type InvoiceLineDraft } from './InvoiceLinesEditor';
import './invoices.css';

interface Defaults {
  taxMode: TaxMode;
  paymentTermsDays: number;
  discountBp: number | null;
  discountDays: number | null;
}

const intOrNull = (v: string): number | null | undefined => {
  if (!v.trim()) return null;
  const n = Number(v.trim());
  return Number.isInteger(n) ? n : undefined;
};

/**
 * Rechnungsentwurf anlegen oder ändern (KON-151). Die Nummer vergibt Wirkia erst beim Ausstellen. Zahlungsziel und Skonto sind mit den Vorgaben aus der Einrichtung
 * (und dem Kunden) vorbelegt und lassen sich je Rechnung ändern. Mit ?kontakt=… ist der Kunde vorbelegt. Eine Rechnung aus einem Auftrag legt die Auftragsseite an.
 */
export function InvoiceEditorPage() {
  const { id } = useParams();
  const isCreate = !id;
  const [params] = useSearchParams();
  const contactParam = isCreate ? params.get('kontakt') : null;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { units, symbols } = useUnits();

  const existing = useQuery({ queryKey: ['invoice-out', id], queryFn: async () => (await api<{ data: SalesInvoice }>('GET', `/invoices-out/${id}`)).data, enabled: !isCreate });
  const defaults = useQuery({ queryKey: ['invoice-out-defaults'], queryFn: async () => (await api<{ data: Defaults }>('GET', '/invoices-out/defaults')).data });
  const preset = useQuery({ queryKey: ['contact', contactParam], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${contactParam}`)).data, enabled: !!contactParam });

  const [contact, setContact] = useState<{ id: string; name: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [customerReference, setCustomerReference] = useState('');
  const [serviceStart, setServiceStart] = useState('');
  const [serviceEnd, setServiceEnd] = useState('');
  const [introText, setIntroText] = useState('');
  const [closingText, setClosingText] = useState('');
  const [termsDays, setTermsDays] = useState('');
  const [skontoPercent, setSkontoPercent] = useState('');
  const [skontoDays, setSkontoDays] = useState('');
  const [discount, setDiscount] = useState('');
  const [exemptions, setExemptions] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<InvoiceLineDraft[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const loaded = useRef(false);

  const taxMode: TaxMode = (isCreate ? defaults.data?.taxMode : existing.data?.taxMode) ?? 'regular_soll';

  useEffect(() => {
    if (!isCreate || loaded.current || !defaults.data) return;
    loaded.current = true;
    setLines([emptyInvoiceLine(defaults.data.taxMode)]);
  }, [isCreate, defaults.data]);
  useEffect(() => {
    if (loaded.current || !existing.data) return;
    const i = existing.data;
    loaded.current = true;
    setContact({ id: i.contactId, name: i.contactName });
    setSubject(i.subject ?? '');
    setCustomerReference(i.customerReference ?? '');
    setServiceStart(i.serviceStart ?? '');
    setServiceEnd(i.serviceEnd ?? '');
    setIntroText(i.introText ?? '');
    setClosingText(i.closingText ?? '');
    setTermsDays(String(i.paymentTermsDays));
    setSkontoPercent(i.discountTermsBp ? bpText(i.discountTermsBp) : '');
    setSkontoDays(i.discountTermsDays ? String(i.discountTermsDays) : '');
    setDiscount(bpText(i.discountBp));
    setExemptions(i.exemptionNotes);
    setLines(i.lines.map(draftFromInvoiceLine));
  }, [existing.data]);
  useEffect(() => {
    if (preset.data && !contact) setContact({ id: preset.data.id, name: preset.data.name });
  }, [preset.data, contact]);

  const save = useMutation({
    mutationFn: () => {
      const days = intOrNull(termsDays);
      const skDays = intOrNull(skontoDays);
      const skBp = skontoPercent.trim() ? parseBp(skontoPercent) : null;
      const base = {
        subject: subject.trim(),
        customerReference: customerReference.trim(),
        introText: introText.trim(),
        closingText: closingText.trim(),
        discountBp: parseBp(discount || '0') ?? 0,
        exemptionNotes: Object.fromEntries(Object.entries(exemptions).filter(([, v]) => v.trim())),
        lines: invoiceLinesBody(lines),
      };
      if (isCreate) {
        return api<{ data: SalesInvoice }>('POST', '/invoices-out', {
          ...base,
          contactId: contact?.id,
          serviceStart: serviceStart || undefined,
          serviceEnd: serviceEnd || undefined,
          ...(days != null ? { paymentTermsDays: days } : {}),
          ...(skBp != null && skDays != null ? { discountTermsBp: skBp, discountTermsDays: skDays } : {}),
        });
      }
      return api<{ data: SalesInvoice }>('PATCH', `/invoices-out/${id}`, {
        ...base,
        serviceStart: serviceStart || null,
        serviceEnd: serviceEnd || null,
        ...(days != null ? { paymentTermsDays: days } : {}),
        discountTermsBp: skBp != null && skDays != null ? skBp : null,
        discountTermsDays: skBp != null && skDays != null ? skDays : null,
      });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      client.setQueryData(['invoice-out', res.data.id], res.data);
      navigate(`/rechnungen/${res.data.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError(lineErrorText(e.fields) ?? 'Bitte prüfen Sie Ihre Eingaben.');
      } else setFormError(e instanceof ApiError ? e.message : 'Die Rechnung konnte nicht gespeichert werden.');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    if (!contact) {
      setErrors({ contactId: 'Bitte einen Kunden wählen.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      return;
    }
    if (lines.length === 0) {
      setFormError('Eine Rechnung braucht mindestens eine Position.');
      return;
    }
    const problem = invoiceLinesProblem(lines, units ? symbols : null);
    if (problem) {
      setFormError(problem);
      return;
    }
    if (intOrNull(termsDays) === undefined || intOrNull(skontoDays) === undefined) {
      setFormError('Zahlungsziel und Skonto-Tage bitte als ganze Zahl angeben.');
      return;
    }
    if (Boolean(skontoPercent.trim()) !== Boolean(skontoDays.trim())) {
      setErrors({ discountTermsDays: 'Skonto braucht Prozent und Tage, oder beides leer.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      return;
    }
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Rechnung nicht gefunden</h1>
        <Link to="/rechnungen">Zur Liste</Link>
      </div>
    );
  }
  if (existing.data && existing.data.status === 'issued') {
    return (
      <div className="card narrow">
        <h1>Nicht änderbar</h1>
        <p>Diese Rechnung ist ausgestellt und lässt sich nicht mehr ändern. Korrekturen laufen über eine Stornorechnung oder Gutschrift.</p>
        <Link to={`/rechnungen/${id}`}>Zur Rechnung</Link>
      </div>
    );
  }
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const back = isCreate ? (contactParam ? `/kontakte/${contactParam}` : '/rechnungen') : `/rechnungen/${id}`;
  const exemptionCodes = [...new Set(lines.map((l) => taxCodeByCode(l.taxCode)).filter((c) => c && ['E', 'AE', 'K', 'G', 'O'].includes(c.category)).map((c) => c!.category))];
  const d = defaults.data;

  return (
    <>
      <p><Link to={back} className="back-link">← {isCreate ? (contactParam ? 'Zum Kontakt' : 'Alle Rechnungen') : 'Zur Rechnung'}</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neue Rechnung' : existing.data?.kind === 'credit_note' ? 'Gutschriftsentwurf bearbeiten' : existing.data?.kind === 'partial' ? 'Anzahlungsrechnung bearbeiten' : existing.data?.kind === 'final' ? 'Schlussrechnung bearbeiten' : 'Rechnungsentwurf bearbeiten'}</h1>
      </div>
      <p className="muted">
        {isCreate
          ? 'Für eine Rechnung ohne Auftrag. Rechnungen zu erledigten Aufträgen legen Sie direkt am Auftrag an. Die Rechnungsnummer vergibt Wirkia erst beim Ausstellen, bis dahin ist die Rechnung ein Entwurf, den Sie frei ändern oder löschen können.'
          : existing.data?.kind === 'credit_note'
            ? `Kürzen Sie die Positionen auf den gutzuschreibenden Betrag. Zusammen dürfen Gutschriften den Betrag der Rechnung ${existing.data.relatedInvoiceNumber ?? ''} nicht übersteigen. Nach dem Ausstellen ist die Gutschrift festgeschrieben.`
            : 'Dieser Entwurf lässt sich frei ändern. Nach dem Ausstellen ist die Rechnung festgeschrieben.'}
      </p>
      {existing.data?.kind === 'final' && existing.data.prepayments.length > 0 && (
        <p className="muted">Beim Ausstellen zieht Wirkia {existing.data.prepayments.length === 1 ? 'die Anzahlung' : 'die Anzahlungen'} {existing.data.prepayments.map((p) => p.number).join(', ')} ab. Der abgezogene Betrag steht auf der Rechnung und der zu zahlende Rest in der Infobox.</p>
      )}
      {existing.data?.kind === 'partial' && <p className="muted">Eine Anzahlungsrechnung fordert einen Teil des Auftragswerts vor der Leistung an. Die spätere Schlussrechnung zieht sie ab.</p>}

      <form className="card offer-editor" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <div className="field">
            <span className="field__label">Kunde <abbr title="Pflichtfeld">*</abbr></span>
            {isCreate ? (
              <ContactPicker value={contact} onChange={setContact} invalid={!!errors.contactId} label="Kunde" hint={<>Neue Kunden legen Sie unter <Link to="/kontakte/neu">Kontakte</Link> an.</>} />
            ) : (
              <strong>{contact?.name}</strong>
            )}
            {contact && <RecipientCard contactId={contact.id} />}
            {error('contactId')}
          </div>
          <label className="field">
            <span>Betreff</span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} aria-invalid={!!errors.subject} />
            {error('subject')}
          </label>
          <label className="field">
            <span>Bestellnummer des Kunden</span>
            <input className="input" value={customerReference} onChange={(e) => setCustomerReference(e.target.value)} maxLength={100} />
            <small className="muted">Bei Rechnungen aus einem Auftrag trägt Wirkia die Nummer des bestätigten Angebots ein, sonst die Auftragsnummer. Sie können sie ändern.</small>
          </label>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>Leistung von</span>
            <input className="input" type="date" value={serviceStart} onChange={(e) => { setServiceStart(e.target.value); if (!serviceEnd || serviceEnd < e.target.value) setServiceEnd(e.target.value); }} aria-invalid={!!errors.serviceStart} />
            {error('serviceStart')}
          </label>
          <label className="field">
            <span>Leistung bis</span>
            <input className="input" type="date" value={serviceEnd} min={serviceStart || undefined} onChange={(e) => setServiceEnd(e.target.value)} aria-invalid={!!errors.serviceEnd} />
            {error('serviceEnd')}
          </label>
          <p className="muted small order-range-hint">Steht als Leistungsdatum auf der Rechnung. Ohne Angabe gilt das Rechnungsdatum.</p>
        </div>

        <label className="field">
          <span>Einleitung</span>
          <textarea className="input textarea textarea--short" value={introText} onChange={(e) => setIntroText(e.target.value)} maxLength={5000} placeholder="Zum Beispiel: Vielen Dank für Ihren Auftrag. Wir berechnen Ihnen folgende Leistungen:" />
        </label>

        <h2 className="offer-editor__h">Positionen</h2>
        <InvoiceLinesEditor lines={lines} setLines={setLines} discount={discount} setDiscount={setDiscount} taxMode={taxMode} fixUnits={isCreate} />

        {exemptionCodes.length > 0 && (
          <>
            <h2 className="offer-editor__h">Grund der Steuerbefreiung</h2>
            <p className="muted small">Bei steuerfreien Umsätzen verlangt die E-Rechnung einen Grund. Tragen Sie ihn je Art ein, zum Beispiel die Rechtsgrundlage.</p>
            <div className="form-grid">
              {exemptionCodes.map((cat) => (
                <label key={cat} className="field">
                  <span>{taxCodeByCode(lines.map((l) => l.taxCode).find((c) => taxCodeByCode(c)?.category === cat)!)?.label}</span>
                  <input className="input" value={exemptions[cat] ?? ''} onChange={(e) => setExemptions((x) => ({ ...x, [cat]: e.target.value }))} maxLength={500} />
                </label>
              ))}
            </div>
          </>
        )}

        <h2 className="offer-editor__h">Zahlungsbedingungen</h2>
        <div className="invoice-terms">
          <label className="field">
            <span className="invoice-label">Zahlungsziel (Tage)<InfoHint label="Zahlungsziel">{FINANCE_HINTS.paymentTerms}</InfoHint></span>
            <input className="input num" inputMode="numeric" value={termsDays} onChange={(e) => setTermsDays(e.target.value)} placeholder={d ? `Vorgabe ${d.paymentTermsDays}` : ''} aria-invalid={!!errors.paymentTermsDays} />
            {error('paymentTermsDays')}
          </label>
          <label className="field">
            <span className="invoice-label">Skonto %<InfoHint label="Skonto">{FINANCE_HINTS.discount}</InfoHint></span>
            <input className="input num" inputMode="decimal" value={skontoPercent} onChange={(e) => setSkontoPercent(e.target.value)} placeholder={d?.discountBp ? `Vorgabe ${bpText(d.discountBp)}` : 'kein Skonto'} aria-invalid={!!errors.discountTermsBp} />
            {error('discountTermsBp')}
          </label>
          <label className="field">
            <span>Skonto bei Zahlung in (Tagen)</span>
            <input className="input num" inputMode="numeric" value={skontoDays} onChange={(e) => setSkontoDays(e.target.value)} placeholder={d?.discountDays ? `Vorgabe ${d.discountDays}` : ''} aria-invalid={!!errors.discountTermsDays} />
            {error('discountTermsDays')}
          </label>
        </div>
        {isCreate && <p className="muted small">Leere Felder übernimmt Wirkia aus den Angaben beim Kunden und aus der Einrichtung.</p>}

        <label className="field">
          <span>Schlusstext</span>
          <textarea className="input textarea textarea--short" value={closingText} onChange={(e) => setClosingText(e.target.value)} maxLength={5000} />
          <small className="muted">Ohne Angabe druckt Wirkia den Fußtext aus der Einrichtung.</small>
        </label>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : isCreate ? 'Entwurf anlegen' : 'Speichern'}</button>
          <Link className="btn btn--secondary" to={back}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
